// VMForm — Step 3 VM configuration modal form.
// Supports add-new (no initialVm) and edit (initialVm provided) modes.
// Template selector pre-populates all fields, including networkConfig.

import { useState } from 'react';
import type {
  VMConfig,
  Tier,
  Environment,
  SiteAssignment,
  WorkloadThreading,
  NetworkSecurityDomain,
  LatencySensitivity,
  TrafficPattern,
  LoadBalancingType,
  VMNetworkConfig,
} from '../../types';
import { VM_TEMPLATES } from '../../lib/calculations/templates';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';
import { FieldTooltip } from '../ui/FieldTooltip';

// ─── Props ────────────────────────────────────────────────────────────────────

interface VMFormProps {
  applicationId: string;
  tierId: string;
  tiers: Tier[];
  initialVm?: VMConfig;
  onSave: (vm: Omit<VMConfig, 'id' | 'applicationId' | 'tierId'>, tierId: string) => void;
  onCancel: () => void;
}

// ─── Default values ───────────────────────────────────────────────────────────

const DEFAULT_NETWORK_CONFIG: VMNetworkConfig = {
  securityDomain: 'internal',
  latencySensitivity: 'standard',
  trafficPattern: 'mixed',
  loadBalancing: 'none',
};

const BLANK_VM: Omit<VMConfig, 'id' | 'applicationId' | 'tierId'> = {
  name: '',
  quantity: 1,
  vcpu: 4,
  memoryGb: 16,
  diskLocalGb: 0,
  diskSanGb: 100,
  diskNasGb: 0,
  diskSanIops: 1000,
  diskReadWriteRatio: 70,
  networkBandwidthMbps: 1000,
  networkConfig: DEFAULT_NETWORK_CONFIG,
  workloadThreading: 'multi_thread',
  cpuSpeedMinGhz: 2.0,
  environment: 'production',
  siteAssignment: 'both',
  notes: '',
};

// ─── Security domain colour pills ─────────────────────────────────────────────

const DOMAIN_COLOURS: Record<NetworkSecurityDomain, string> = {
  internal: 'bg-blue-900/40 text-blue-300 border border-blue-700',
  dmz: 'bg-orange-900/40 text-orange-300 border border-orange-700',
  external: 'bg-red-900/40 text-red-300 border border-red-700',
  management: 'bg-purple-900/40 text-purple-300 border border-purple-700',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function VMForm({ applicationId: _applicationId, tierId: initialTierId, tiers, initialVm, onSave, onCancel }: VMFormProps) {
  const [selectedTierId, setSelectedTierId] = useState(initialVm?.tierId ?? initialTierId);
  const [form, setForm] = useState<Omit<VMConfig, 'id' | 'applicationId' | 'tierId'>>(
    initialVm
      ? {
          name: initialVm.name,
          quantity: initialVm.quantity,
          vcpu: initialVm.vcpu,
          memoryGb: initialVm.memoryGb,
          diskLocalGb: initialVm.diskLocalGb,
          diskSanGb: initialVm.diskSanGb,
          diskNasGb: initialVm.diskNasGb,
          diskSanIops: initialVm.diskSanIops,
          diskReadWriteRatio: initialVm.diskReadWriteRatio,
          networkBandwidthMbps: initialVm.networkBandwidthMbps,
          networkConfig: { ...initialVm.networkConfig },
          workloadThreading: initialVm.workloadThreading,
          cpuSpeedMinGhz: initialVm.cpuSpeedMinGhz,
          environment: initialVm.environment,
          siteAssignment: initialVm.siteAssignment,
          notes: initialVm.notes ?? '',
        }
      : { ...BLANK_VM, networkConfig: { ...DEFAULT_NETWORK_CONFIG } },
  );

  const [networkOpen, setNetworkOpen] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // ── Dirty detection ───────────────────────────────────────────────────────
  // Edit mode: dirty if any field differs from the original VM.
  // Add mode: dirty if the user has typed a name (proxy for intentional edits).
  const isDirty = initialVm
    ? (
        form.name !== initialVm.name ||
        form.quantity !== initialVm.quantity ||
        form.vcpu !== initialVm.vcpu ||
        form.memoryGb !== initialVm.memoryGb ||
        form.diskLocalGb !== initialVm.diskLocalGb ||
        form.diskSanGb !== initialVm.diskSanGb ||
        form.diskNasGb !== initialVm.diskNasGb ||
        form.diskSanIops !== initialVm.diskSanIops ||
        form.diskReadWriteRatio !== initialVm.diskReadWriteRatio ||
        form.networkBandwidthMbps !== initialVm.networkBandwidthMbps ||
        form.workloadThreading !== initialVm.workloadThreading ||
        form.cpuSpeedMinGhz !== initialVm.cpuSpeedMinGhz ||
        form.environment !== initialVm.environment ||
        form.siteAssignment !== initialVm.siteAssignment ||
        form.notes !== (initialVm.notes ?? '') ||
        form.networkConfig.securityDomain !== initialVm.networkConfig.securityDomain ||
        form.networkConfig.latencySensitivity !== initialVm.networkConfig.latencySensitivity ||
        form.networkConfig.trafficPattern !== initialVm.networkConfig.trafficPattern ||
        form.networkConfig.loadBalancing !== initialVm.networkConfig.loadBalancing ||
        form.networkConfig.loadBalancingSessionsPerSec !== initialVm.networkConfig.loadBalancingSessionsPerSec
      )
    : form.name !== '';

  function handleCancel() {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      onCancel();
    }
  }

  // ── Template selection ────────────────────────────────────────────────────

  function applyTemplate(templateName: string) {
    if (!templateName) return;
    const tpl = VM_TEMPLATES.find(t => t.templateName === templateName);
    if (!tpl) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { templateName: _templateName, ...rest } = tpl;
    setForm({ ...rest, notes: '' });
    setErrors({});
  }

  // ── Field helpers ─────────────────────────────────────────────────────────

  function setField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  function setNetworkField<K extends keyof VMNetworkConfig>(key: K, value: VMNetworkConfig[K]) {
    setForm(prev => ({
      ...prev,
      networkConfig: { ...prev.networkConfig, [key]: value },
    }));
  }

  function numField(key: keyof typeof form, value: string) {
    const n = parseFloat(value);
    if (!isNaN(n)) setField(key, n as never);
  }

  function intField(key: keyof typeof form, value: string) {
    const n = parseInt(value, 10);
    if (!isNaN(n)) setField(key, n as never);
  }

  // ── Validation ────────────────────────────────────────────────────────────

  function validate(): boolean {
    const errs: typeof errors = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (form.quantity < 1) errs.quantity = 'Quantity must be ≥ 1';
    if (form.vcpu < 1) errs.vcpu = 'vCPU must be ≥ 1';
    if (form.memoryGb < 1) errs.memoryGb = 'Memory must be ≥ 1 GB';
    if (form.diskReadWriteRatio < 0 || form.diskReadWriteRatio > 100)
      errs.diskReadWriteRatio = 'R/W ratio must be 0–100';
    if (form.networkBandwidthMbps < 1) errs.networkBandwidthMbps = 'Bandwidth must be ≥ 1 Mbps';
    if (form.cpuSpeedMinGhz <= 0) errs.cpuSpeedMinGhz = 'CPU speed must be > 0 GHz';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validate()) onSave(form, selectedTierId);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[#0d1f30] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-[rgba(76,92,104,0.35)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[rgba(76,92,104,0.35)] flex items-center justify-between bg-[#132840]">
          <h2 className="text-lg font-semibold text-[#E8EDF2]">
            {initialVm ? 'Edit VM' : 'Add VM'}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className="text-[#8FA4B5] hover:text-[#E8EDF2] text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-6">

          {/* Template selector */}
          {!initialVm && (
            <div>
              <label className="block text-sm font-medium text-[#8FA4B5] mb-1">
                Start from template <span className="text-[#4C5C68] font-normal">(optional)</span>
              </label>
              <select
                className="w-full border border-[rgba(76,92,104,0.35)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[rgba(245,166,33,0.5)] bg-[#0a1929] text-[#E8EDF2]"
                defaultValue=""
                onChange={e => applyTemplate(e.target.value)}
              >
                <option value="">— blank —</option>
                {VM_TEMPLATES.map(t => (
                  <option key={t.templateName} value={t.templateName}>
                    {t.templateName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── Section: Identity ────────────────────────────────────────── */}
          <fieldset className="border border-[rgba(76,92,104,0.35)] rounded-md p-4">
            <legend className="px-1 text-[11px] font-semibold text-[#4C5C68] uppercase tracking-[0.8px] font-[Poppins]">
              Identity
            </legend>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2">
                <Label htmlFor="vm-name" tooltip="A unique label for this virtual machine. Used in the BOM and all diagram outputs. Use a descriptive name that reflects the VM's role (e.g. prod-app-01, db-primary).">VM Name</Label>
                <input
                  id="vm-name"
                  type="text"
                  className={inputCls(!!errors.name)}
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="e.g. prod-app-01"
                />
                {errors.name && <FieldError>{errors.name}</FieldError>}
              </div>
              <div>
                <Label htmlFor="vm-quantity" tooltip="Number of identical VMs in this group. The sizing engine multiplies all resource values (vCPU, RAM, disk, IOPS, bandwidth) by this quantity.">Quantity</Label>
                <input
                  id="vm-quantity"
                  type="number"
                  min={1}
                  className={inputCls(!!errors.quantity)}
                  value={form.quantity}
                  onChange={e => intField('quantity', e.target.value)}
                />
                {errors.quantity && <FieldError>{errors.quantity}</FieldError>}
              </div>
              <div>
                <Label htmlFor="vm-tier">Tier</Label>
                <select
                  id="vm-tier"
                  aria-label="Tier"
                  className={inputCls(false)}
                  value={selectedTierId}
                  onChange={e => setSelectedTierId(e.target.value)}
                >
                  {tiers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="vm-env" tooltip="Determines which overcommit ratios apply to this VM. Production uses the global ratios; other environments can have per-environment overrides in Step 4.">Environment</Label>
                <select
                  id="vm-env"
                  className={inputCls(false)}
                  value={form.environment}
                  onChange={e => setField('environment', e.target.value as Environment)}
                >
                  <option value="production">Production</option>
                  <option value="pre_production">Pre-Production</option>
                  <option value="development">Development</option>
                </select>
              </div>
              <div>
                <Label htmlFor="vm-site" tooltip="Controls which datacenter sites size for this VM. 'Both' sizes compute and storage at both Primary and DR. 'Primary only' or 'DR only' restricts to one site.">Site Assignment</Label>
                <select
                  id="vm-site"
                  className={inputCls(false)}
                  value={form.siteAssignment}
                  onChange={e => setField('siteAssignment', e.target.value as SiteAssignment)}
                >
                  <option value="both">Both (Primary + DR)</option>
                  <option value="primary">Primary only</option>
                  <option value="dr">DR only</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* ── Section: Compute ─────────────────────────────────────────── */}
          <fieldset className="border border-[rgba(76,92,104,0.35)] rounded-md p-4">
            <legend className="px-1 text-[11px] font-semibold text-[#4C5C68] uppercase tracking-[0.8px] font-[Poppins]">
              Compute
            </legend>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <Label htmlFor="vm-vcpu" tooltip="Virtual CPU count assigned to this VM. The engine divides this by the vCPU overcommit ratio (Step 4) to derive physical core demand.">vCPU</Label>
                <input
                  id="vm-vcpu"
                  type="number"
                  min={1}
                  className={inputCls(!!errors.vcpu)}
                  value={form.vcpu}
                  onChange={e => intField('vcpu', e.target.value)}
                />
                {errors.vcpu && <FieldError>{errors.vcpu}</FieldError>}
              </div>
              <div>
                <Label htmlFor="vm-mem" tooltip="RAM allocated to this VM in gigabytes. Divided by the memory overcommit ratio (Step 4) to derive physical memory demand.">Memory (GB)</Label>
                <input
                  id="vm-mem"
                  type="number"
                  min={1}
                  step="0.5"
                  className={inputCls(!!errors.memoryGb)}
                  value={form.memoryGb}
                  onChange={e => numField('memoryGb', e.target.value)}
                />
                {errors.memoryGb && <FieldError>{errors.memoryGb}</FieldError>}
              </div>
              <div>
                <Label htmlFor="vm-cpu-speed" tooltip="Minimum clock speed required by this workload. Used to filter compatible host hardware in the BOM spec notes. Does not affect host count.">Min CPU Speed (GHz)</Label>
                <input
                  id="vm-cpu-speed"
                  type="number"
                  min={0.1}
                  step={0.1}
                  className={inputCls(!!errors.cpuSpeedMinGhz)}
                  value={form.cpuSpeedMinGhz}
                  onChange={e => numField('cpuSpeedMinGhz', e.target.value)}
                />
                {errors.cpuSpeedMinGhz && <FieldError>{errors.cpuSpeedMinGhz}</FieldError>}
              </div>
              <div>
                <Label htmlFor="vm-threading" tooltip="Single-threaded workloads are bound to one core and cannot benefit from a high overcommit ratio. Multi-threaded workloads spread across cores and tolerate higher overcommit.">Workload Threading</Label>
                <select
                  id="vm-threading"
                  className={inputCls(false)}
                  value={form.workloadThreading}
                  onChange={e => setField('workloadThreading', e.target.value as WorkloadThreading)}
                >
                  <option value="multi_thread">Multi-threaded</option>
                  <option value="single_thread">Single-threaded</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* ── Section: Storage ─────────────────────────────────────────── */}
          <fieldset className="border border-[rgba(76,92,104,0.35)] rounded-md p-4">
            <legend className="px-1 text-[11px] font-semibold text-[#4C5C68] uppercase tracking-[0.8px] font-[Poppins]">
              Storage
            </legend>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <Label htmlFor="vm-disk-local" tooltip="Ephemeral storage hosted directly on the hypervisor (not shared). Included in rack population diagrams but not in SAN/NAS capacity calculations.">Local Disk (GB)</Label>
                <input
                  id="vm-disk-local"
                  type="number"
                  min={0}
                  className={inputCls(false)}
                  value={form.diskLocalGb}
                  onChange={e => numField('diskLocalGb', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vm-disk-san" tooltip="Shared block storage (SAN/HCI). Added to the usable storage pool, then grown and converted to raw capacity using the ratios in Step 4.">SAN Disk (GB)</Label>
                <input
                  id="vm-disk-san"
                  type="number"
                  min={0}
                  className={inputCls(false)}
                  value={form.diskSanGb}
                  onChange={e => numField('diskSanGb', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vm-disk-nas" tooltip="Shared file storage (NAS). Sized separately from SAN. Growth buffer and usable-to-raw ratio (Step 4) are applied independently.">NAS Disk (GB)</Label>
                <input
                  id="vm-disk-nas"
                  type="number"
                  min={0}
                  className={inputCls(false)}
                  value={form.diskNasGb}
                  onChange={e => numField('diskNasGb', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vm-iops" tooltip="Peak I/O operations per second required on the SAN. The engine applies the RAID write penalty factor (Step 4) based on the read/write ratio to derive effective IOPS demand.">SAN IOPS</Label>
                <input
                  id="vm-iops"
                  type="number"
                  min={0}
                  className={inputCls(false)}
                  value={form.diskSanIops}
                  onChange={e => numField('diskSanIops', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vm-rw-ratio" tooltip="Percentage of SAN I/O that is reads. Write % is automatically derived (100 − Read %). A higher write proportion increases effective IOPS demand due to the RAID write penalty.">
                  Read/Write Ratio — Read %
                </Label>
                <input
                  id="vm-rw-ratio"
                  type="number"
                  min={0}
                  max={100}
                  className={inputCls(!!errors.diskReadWriteRatio)}
                  value={form.diskReadWriteRatio}
                  onChange={e => numField('diskReadWriteRatio', e.target.value)}
                />
                <p className="text-xs text-[#4C5C68] mt-0.5">
                  Write % = {100 - form.diskReadWriteRatio}
                </p>
                {errors.diskReadWriteRatio && <FieldError>{errors.diskReadWriteRatio}</FieldError>}
              </div>
            </div>
          </fieldset>

          {/* ── Section: Network ─────────────────────────────────────────── */}
          <fieldset className="border border-[rgba(76,92,104,0.35)] rounded-md p-4">
            <legend className="px-1 text-[11px] font-semibold text-[#4C5C68] uppercase tracking-[0.8px] font-[Poppins]">
              Network
            </legend>
            <div className="mt-2">
              <Label htmlFor="vm-bw" tooltip="Peak network throughput required by this VM. Split by traffic pattern into east-west and north-south pools, then summed across all VMs to size uplinks.">Bandwidth (Mbps)</Label>
              <input
                id="vm-bw"
                type="number"
                min={1}
                className={inputCls(!!errors.networkBandwidthMbps)}
                value={form.networkBandwidthMbps}
                onChange={e => numField('networkBandwidthMbps', e.target.value)}
              />
              {errors.networkBandwidthMbps && <FieldError>{errors.networkBandwidthMbps}</FieldError>}
            </div>

            {/* Network Configuration — collapsible */}
            <div className="mt-4">
              <button
                type="button"
                className="flex items-center gap-2 text-sm font-medium text-[#CEECF8] hover:text-[#E8EDF2]"
                onClick={() => setNetworkOpen(o => !o)}
                aria-expanded={networkOpen}
              >
                <span>{networkOpen ? '▼' : '▶'}</span>
                Network Configuration
                <span className="ml-2 text-xs text-[#8FA4B5] font-normal">
                  {domainLabel(form.networkConfig.securityDomain)} ·{' '}
                  {latencyLabel(form.networkConfig.latencySensitivity)} ·{' '}
                  {trafficLabel(form.networkConfig.trafficPattern)}
                </span>
              </button>

              {networkOpen && (
                <div className="mt-3 space-y-4 pl-4 border-l-2 border-[rgba(76,92,104,0.35)]">

                  {/* Security Domain */}
                  <div>
                    <Label tooltip="Network zone used for firewall placement and VLAN segmentation. The number of distinct domains across all VMs determines the required VLAN count.">Security Domain</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(['internal', 'dmz', 'external', 'management'] as NetworkSecurityDomain[]).map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setNetworkField('securityDomain', d)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                            form.networkConfig.securityDomain === d
                              ? DOMAIN_COLOURS[d]
                              : 'bg-[#0a1929] text-[#8FA4B5] border border-[rgba(76,92,104,0.35)]'
                          }`}
                        >
                          {domainLabel(d)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Latency Sensitivity */}
                  <div>
                    <Label tooltip="Standard: no hardware affinity required. Low Latency: VM should be on hosts with fast interconnects. Ultra-Low Latency: requires dedicated hardware affinity, which reduces effective cluster capacity — a warning appears in the results.">Latency Sensitivity</Label>
                    <div className="flex gap-4 mt-1">
                      {(['standard', 'low_latency', 'ultra_low_latency'] as LatencySensitivity[]).map(l => (
                        <label key={l} className="flex items-center gap-1.5 cursor-pointer text-[#E8EDF2]">
                          <input
                            type="radio"
                            name="latency"
                            value={l}
                            checked={form.networkConfig.latencySensitivity === l}
                            onChange={() => setNetworkField('latencySensitivity', l)}
                            className="accent-blue-600"
                          />
                          <span className={`text-sm ${l === 'ultra_low_latency' ? 'text-red-400 font-medium' : 'text-[#E8EDF2]'}`}>
                            {latencyLabel(l)}
                          </span>
                        </label>
                      ))}
                    </div>
                    {form.networkConfig.latencySensitivity === 'ultra_low_latency' && (
                      <p className="mt-1 text-xs text-red-400">
                        Ultra-low latency VMs reduce effective cluster capacity — a warning will appear in the results.
                      </p>
                    )}
                  </div>

                  {/* Traffic Pattern */}
                  <div>
                    <Label tooltip="East-West traffic stays within the datacenter (VM-to-VM). North-South crosses the datacenter boundary (client-facing). Mixed contributes to both pools.">Traffic Pattern</Label>
                    <div className="flex gap-4 mt-1">
                      {(['mixed', 'east_west', 'north_south'] as TrafficPattern[]).map(p => (
                        <label key={p} className="flex items-center gap-1.5 cursor-pointer text-[#E8EDF2]">
                          <input
                            type="radio"
                            name="traffic"
                            value={p}
                            checked={form.networkConfig.trafficPattern === p}
                            onChange={() => setNetworkField('trafficPattern', p)}
                            className="accent-blue-600"
                          />
                          <span className="text-sm text-[#E8EDF2]">{trafficLabel(p)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Load Balancing */}
                  <div>
                    <Label htmlFor="vm-lb" tooltip="Whether this VM group sits behind a load balancer. If selected, a Load Balancing BOM line item is added. 'Sessions / second' refines the LB sizing spec.">Load Balancing</Label>
                    <select
                      id="vm-lb"
                      className={inputCls(false)}
                      value={form.networkConfig.loadBalancing}
                      onChange={e => {
                        const lb = e.target.value as LoadBalancingType;
                        setNetworkField('loadBalancing', lb);
                        if (lb === 'none') {
                          setNetworkField('loadBalancingSessionsPerSec', undefined);
                        }
                      }}
                    >
                      <option value="none">None</option>
                      <option value="internal_l4">Internal L4</option>
                      <option value="internal_l7">Internal L7</option>
                      <option value="external_l4">External L4</option>
                      <option value="external_l7">External L7</option>
                    </select>

                    {form.networkConfig.loadBalancing !== 'none' && (
                      <div className="mt-2">
                        <Label htmlFor="vm-lb-sessions">Sessions / second</Label>
                        <input
                          id="vm-lb-sessions"
                          type="number"
                          min={1}
                          className={inputCls(false)}
                          value={form.networkConfig.loadBalancingSessionsPerSec ?? ''}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            setNetworkField('loadBalancingSessionsPerSec', isNaN(v) ? undefined : v);
                          }}
                          placeholder="e.g. 1000"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </fieldset>

          {/* Notes */}
          <div>
            <Label htmlFor="vm-notes">Notes <span className="text-[#4C5C68] font-normal">(optional)</span></Label>
            <textarea
              id="vm-notes"
              rows={2}
              className="w-full border border-[rgba(76,92,104,0.35)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[rgba(245,166,33,0.5)] resize-none bg-[#0a1929] text-[#E8EDF2]"
              value={form.notes ?? ''}
              onChange={e => setField('notes', e.target.value)}
              placeholder="Any notes about this VM's role or constraints"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[rgba(76,92,104,0.35)] flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm border border-[rgba(76,92,104,0.35)] rounded-md text-[#8FA4B5] hover:bg-[#0d1f30]"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="vm-form-inner"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm bg-[#F5A621] text-[#021526] rounded-md hover:bg-[#e0951d] font-semibold"
          >
            {initialVm ? 'Save changes' : 'Add VM'}
          </button>
        </div>
      </div>

      <UnsavedChangesDialog
        open={showDiscardDialog}
        onStay={() => setShowDiscardDialog(false)}
        onLeave={onCancel}
      />
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────────────

function Label({ htmlFor, tooltip, children }: { htmlFor?: string; tooltip?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center text-sm font-medium text-[#8FA4B5] mb-1">
      {children}
      {tooltip && <FieldTooltip text={tooltip} />}
    </label>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs text-[#E05555]">{children}</p>;
}

function inputCls(error: boolean) {
  return `w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[rgba(245,166,33,0.5)] bg-[#0a1929] text-[#E8EDF2] ${
    error ? 'border-[#E05555]' : 'border-[rgba(76,92,104,0.35)]'
  }`;
}

function domainLabel(d: NetworkSecurityDomain) {
  return { internal: 'Internal', dmz: 'DMZ', external: 'External', management: 'Management' }[d];
}

function latencyLabel(l: LatencySensitivity) {
  return { standard: 'Standard', low_latency: 'Low Latency', ultra_low_latency: 'Ultra-Low Latency' }[l];
}

function trafficLabel(p: TrafficPattern) {
  return { mixed: 'Mixed', east_west: 'East-West', north_south: 'North-South' }[p];
}
